import * as Sentry from "@sentry/react";
import type { ClientLocationPlan } from "api-types/locationPlans/locationPlansResponses";
import type { ClientMasterPlan, ClientMasterPlanReportingUnit } from "api-types/masterPlans/masterPlansResponses";
import type { FilterPair } from "common/utils/search";
import {
  MasterPlanFilter,
  MasterPlanStatusValue,
  MasterPlanTypeValue,
} from "lib/components/builders/communities/plans/masterPlanFiltersConstants";
import {
  MASTER_PLAN_UNRESOLVED_MESSAGE,
  NO_MULTI_UNIT_PLANS_AVAILABLE_MESSAGE,
  noPlansMatchingSearchMessage,
} from "lib/constants/messages";

export function sortReportingUnitsBySortIndex(
  reportingUnits: ClientMasterPlanReportingUnit[],
): ClientMasterPlanReportingUnit[] {
  return [...reportingUnits].sort((a, b) => a.sortIndex - b.sortIndex);
}

/** Sorted reporting units when the master plan is already available. */
export function getSortedReportingUnits(
  masterPlan: ClientMasterPlan | null | undefined,
): ClientMasterPlanReportingUnit[] {
  return sortReportingUnitsBySortIndex(masterPlan?.reportingUnits ?? []);
}

const reportedMasterPlanLookupMisses = new Set<string>();

/** Clears deduped Sentry reports — for tests only. */
export function __resetMasterPlanLookupMissReportsForTests(): void {
  reportedMasterPlanLookupMisses.clear();
}

function getMasterPlanIdForLookup(plan: ClientMasterPlan | ClientLocationPlan): string {
  return "masterPlanId" in plan && plan.masterPlanId ? plan.masterPlanId : plan.id;
}

/** A location plan defers to its master plan for reporting units; a master plan is its own source. */
function findMasterPlan(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): ClientMasterPlan | undefined {
  const masterPlanId = getMasterPlanIdForLookup(plan);
  return masterPlans.find((mp) => mp.id === masterPlanId);
}

function reportMasterPlanLookupMiss(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlanId: string,
  masterPlansCount: number,
): void {
  const dedupeKey = `${plan.id}:${masterPlanId}`;
  if (reportedMasterPlanLookupMisses.has(dedupeKey)) {
    return;
  }
  reportedMasterPlanLookupMisses.add(dedupeKey);
  Sentry.captureException(new Error("Master plan not found for reporting unit lookup"), {
    extra: {
      planId: plan.id,
      masterPlanId,
      masterPlansCount,
    },
  });
}

function resolveMasterPlan(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): ClientMasterPlan | undefined {
  const masterPlan = findMasterPlan(plan, masterPlans);
  if (!masterPlan) {
    reportMasterPlanLookupMiss(plan, getMasterPlanIdForLookup(plan), masterPlans.length);
  }
  return masterPlan;
}

/**
 * Resolves sorted reporting units for a plan by looking up its master plan.
 * Returns undefined when the master plan is missing (distinct from [] for single-family).
 */
export function getMasterPlanReportingUnits(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): ClientMasterPlanReportingUnit[] | undefined {
  const masterPlan = resolveMasterPlan(plan, masterPlans);
  if (!masterPlan) {
    return undefined;
  }
  return sortReportingUnitsBySortIndex(masterPlan.reportingUnits ?? []);
}

/** Unit count for a plan, without the sort `getMasterPlanReportingUnits` does for display. */
function getPlanUnitCount(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): number | undefined {
  const masterPlan = resolveMasterPlan(plan, masterPlans);
  if (!masterPlan) {
    return undefined;
  }
  return masterPlan.reportingUnits?.length ?? 0;
}

/** Plan ID → unit count, for showing unit counts and comparing them against available lots. */
export function getUnitCountsByPlanId(
  plans: (ClientMasterPlan | ClientLocationPlan)[],
  masterPlans: ClientMasterPlan[],
): Record<string, number | undefined> {
  return Object.fromEntries(plans.map((plan) => [plan.id, getPlanUnitCount(plan, masterPlans)]));
}

/** True when the given master plan already has reporting units. Prefer when the master is in hand. */
export function isMasterPlanMultiUnit(masterPlan: ClientMasterPlan | undefined): boolean {
  return (masterPlan?.reportingUnits?.length ?? 0) > 0;
}

/**
 * True when a master or location plan has units, resolving the master from `masterPlans` when needed.
 * Returns undefined when the master plan cannot be resolved.
 * Use `isMasterPlanMultiUnit` instead when you already have the master plan object.
 */
export function isMultiUnitPlan(
  plan: ClientMasterPlan | ClientLocationPlan,
  masterPlans: ClientMasterPlan[],
): boolean | undefined {
  const unitCount = getPlanUnitCount(plan, masterPlans);
  if (unitCount === undefined) {
    return undefined;
  }
  return unitCount > 0;
}

/**
 * Narrows `plans` to those eligible for a multi-unit location, and flags whether any offered
 * plan's master plan couldn't be resolved (a lookup failure, not a legitimate "no multi-unit
 * plans" state — see `isMultiUnitPlan`). For non-multi-unit locations, every plan is eligible and
 * master plans are never resolved, so lookup failures can't affect the result.
 */
export function partitionEligiblePlans(
  plans: (ClientMasterPlan | ClientLocationPlan)[],
  masterPlans: ClientMasterPlan[],
  isMultiUnitLocation: boolean,
): {
  eligiblePlans: (ClientMasterPlan | ClientLocationPlan)[];
  offeredPlansIncludeUnresolvableMaster: boolean;
} {
  if (!isMultiUnitLocation) {
    return { eligiblePlans: plans, offeredPlansIncludeUnresolvableMaster: false };
  }
  let offeredPlansIncludeUnresolvableMaster = false;
  const eligiblePlans = plans.filter((plan) => {
    const result = isMultiUnitPlan(plan, masterPlans);
    if (result === undefined) {
      offeredPlansIncludeUnresolvableMaster = true;
    }
    return result === true;
  });
  return { eligiblePlans, offeredPlansIncludeUnresolvableMaster };
}

/** Empty state for the plan selection step, distinguishing a real "no matches" from a master-plan lookup failure. */
export function getPlanSelectionEmptyMessage(
  isMultiUnitLocation: boolean,
  eligiblePlanCount: number,
  offeredPlansIncludeUnresolvableMaster: boolean,
  locationPlanName: string,
): string {
  if (isMultiUnitLocation && eligiblePlanCount === 0) {
    if (offeredPlansIncludeUnresolvableMaster) {
      return MASTER_PLAN_UNRESOLVED_MESSAGE;
    }
    return NO_MULTI_UNIT_PLANS_AVAILABLE_MESSAGE;
  }
  return noPlansMatchingSearchMessage(locationPlanName);
}

/** Display label for a master plan's type, used by the Plan Library's plan-type column. */
export function getMasterPlanTypeLabel(masterPlan: ClientMasterPlan): string {
  return isMasterPlanMultiUnit(masterPlan) ? "Multi-unit" : "Single family";
}

/**
 * Resolves the value a master plan contributes to each Plan Library filter, for
 * `filterListBySearchAndFilters`. Returns undefined for unknown filter names so the
 * plan simply fails to match rather than throwing.
 */
export function getMasterPlanFilterValue(masterPlan: ClientMasterPlan, filterName: string): string | undefined {
  if (filterName === MasterPlanFilter.STATUS) {
    return masterPlan.archivedAt ? MasterPlanStatusValue.ARCHIVED : MasterPlanStatusValue.ACTIVE;
  }
  if (filterName === MasterPlanFilter.PLAN_TYPE) {
    return isMasterPlanMultiUnit(masterPlan) ? MasterPlanTypeValue.MULTI_UNIT : MasterPlanTypeValue.SINGLE_FAMILY;
  }
  return undefined;
}

/** The values currently selected for one filter, or an empty array when the filter is unset. */
export function getSelectedFilterValues(filters: FilterPair[], filterName: MasterPlanFilter): string[] {
  return filters.find(([name]) => name === filterName)?.[1] ?? [];
}
